/**
 * AdminCrossSchoolAnalytics — platform-wide ecosystem roll-up (super admin).
 *
 * Reads /api/superadmin/cross-school-analytics and presents:
 *   - Module totals across all tenants + 30-day deltas
 *   - Per-tenant KPI table, sorted by students desc
 *   - Revenue summary (POS sales + fees collected last 30d)
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layouts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Loader2, Globe2, TrendingUp, DollarSign } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtN = (n) => Number(n || 0).toLocaleString();

const AdminCrossSchoolAnalytics = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['cross-school-analytics'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/superadmin/cross-school-analytics`, { headers: authHeaders() });
      if (!r.ok) throw new Error('Failed to load analytics');
      return r.json();
    },
    refetchInterval: 60000,
  });

  if (isLoading) {
    return (
      <DashboardLayout title="Cross-school Analytics">
        <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      </DashboardLayout>
    );
  }
  if (error || !data) {
    return (
      <DashboardLayout title="Cross-school Analytics">
        <div className="py-10 text-center text-sm text-rose-600">Could not load analytics</div>
      </DashboardLayout>
    );
  }

  const s = data.summary;
  const tenants = data.per_tenant || [];

  return (
    <DashboardLayout title="Cross-school Analytics">
      <div className="space-y-6" data-testid="cross-school-analytics-page">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Globe2 className="h-5 w-5 text-indigo-600" /> Cross-school Analytics
            </h1>
            <p className="text-xs text-slate-500">Ecosystem health across all {s.total_tenants} tenants — refreshed every minute.</p>
          </div>
        </div>

        {/* Revenue headline strip */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border-emerald-200">
            <CardContent className="pt-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs text-emerald-800 font-medium uppercase tracking-wide">POS sales · 30d</div>
                  <div className="text-2xl font-bold text-emerald-900 mt-1" data-testid="kpi-pos-sales-30d">
                    {fmtMoney(s.revenue_30d.pos_sales)}
                  </div>
                </div>
                <DollarSign className="h-6 w-6 text-emerald-600" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-indigo-500/10 to-indigo-500/5 border-indigo-200">
            <CardContent className="pt-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs text-indigo-800 font-medium uppercase tracking-wide">Fees collected · 30d</div>
                  <div className="text-2xl font-bold text-indigo-900 mt-1" data-testid="kpi-fees-30d">
                    {fmtMoney(s.revenue_30d.fees_collected)}
                  </div>
                </div>
                <TrendingUp className="h-6 w-6 text-indigo-600" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-slate-500/10 to-slate-500/5 border-slate-200">
            <CardContent className="pt-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs text-slate-800 font-medium uppercase tracking-wide">Tenants</div>
                  <div className="text-2xl font-bold text-slate-900 mt-1" data-testid="kpi-total-tenants">
                    {s.total_tenants}
                  </div>
                </div>
                <Globe2 className="h-6 w-6 text-slate-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Module totals grid */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Module totals across all tenants</CardTitle>
            <CardDescription>Record counts, with the last-30-day delta highlighted.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {s.module_totals.map((m) => (
                <div key={m.collection} className="rounded-lg border bg-white p-3" data-testid={`module-total-${m.collection}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                        <span>{m.icon}</span>
                        <span>{m.label}</span>
                      </div>
                      <div className="text-xl font-bold text-slate-900 mt-0.5">{fmtN(m.total)}</div>
                    </div>
                    {m.new_30d > 0 && (
                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">
                        +{fmtN(m.new_30d)} · 30d
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Per-tenant roll-up */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Per-tenant snapshot</CardTitle>
            <CardDescription>Click any school to open its full admin view.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>School</TableHead>
                  <TableHead className="text-right">Students</TableHead>
                  <TableHead className="text-right">Teachers</TableHead>
                  <TableHead className="text-right">Staff</TableHead>
                  <TableHead className="text-right">Housing</TableHead>
                  <TableHead className="text-right">Library</TableHead>
                  <TableHead className="text-right">POS products</TableHead>
                  <TableHead className="text-right">New students · 30d</TableHead>
                  <TableHead className="text-right">Behaviour · 30d</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-sm text-slate-500">No tenants yet</TableCell></TableRow>
                ) : tenants.map((t) => (
                  <TableRow
                    key={t.tenant_id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => navigate(`/admin/schools/${t.tenant_id}`)}
                    data-testid={`tenant-row-${t.tenant_id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.color }} />
                        <div>
                          <div className="font-medium">{t.name}</div>
                          {t.code && <div className="text-[10px] text-slate-500 font-mono">{t.code}</div>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono">{fmtN(t.students)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtN(t.teachers)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtN(t.staff)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtN(t.housing_blocks)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtN(t.library_books)}</TableCell>
                    <TableCell className="text-right font-mono">{fmtN(t.pos_products)}</TableCell>
                    <TableCell className="text-right">
                      {t.new_students_30d > 0
                        ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">+{t.new_students_30d}</Badge>
                        : <span className="text-slate-400 text-xs">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      {t.behaviour_30d > 0
                        ? <Badge className="bg-amber-100 text-amber-700 border-amber-200">{t.behaviour_30d}</Badge>
                        : <span className="text-slate-400 text-xs">—</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <p className="text-[10px] text-slate-400 text-right">Generated {new Date(s.generated_at).toLocaleString()}</p>
      </div>
    </DashboardLayout>
  );
};

export default AdminCrossSchoolAnalytics;
